import AddCircleIcon from '@mui/icons-material/AddCircle';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Button,createTheme,Skeleton,Stack,TableCell,ThemeProvider,Typography } from "@mui/material";
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { useQueries } from "@tanstack/react-query";
import MUIDataTable,{ MUIDataTableOptions } from "mui-datatables";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import AttToken from "../../helpers/attToken";
import Manages from "../../service/Manages";
import ServiceTypes from "../../service/ServiceTypes";
import Teams from "../../service/Teams";
import Users from "../../service/Users";
import { newManageType } from "../../types/managements/types";
import NewMgmtModal from "./Modal/NewMgmtModal";

export type manageType = {
  dataAlteracao?: number,
  dataCriacao?: number,
  dataDelecao?: number,
  empresa: string,
  equipes?: { id: string, nome: string }[],
  gerentes?: string[],
  id?: string,
  nome?: string,
  status: boolean,
  tiposDeServico?: { id: string, nome: string, conjuntosAssociados: string[] }[],
  geral?: boolean
};

const managesService = new Manages();
const userService = new Users();
const teamService = new Teams();
const serviceTypeSerice = new ServiceTypes();

function TableComp({ gerencia }: { gerencia: newManageType }) {
  const maxLength = Math.max(
    gerencia?.gerentes?.length || 0,
    gerencia?.equipes?.flatMap((ele) => ele.nome)?.length || 0,
    gerencia?.tiposDeServico?.length || 0
  );

  const rows = Array?.from({ length: maxLength }, (_, index) => (
    <TableRow key={index}>
      <TableCell width='30%'>
        <Typography fontSize='small' sx={{ wordBreak: 'break-word' }}>
          {gerencia?.gerentes?.[index] || ''}
        </Typography>
      </TableCell>
      <TableCell width='30%'>
        <Typography fontSize='small' sx={{ wordBreak: 'break-word' }}>
          {gerencia?.equipes?.[index]?.nome || ''}
        </Typography>
      </TableCell>
      <TableCell width='30%'>
        <Typography fontSize='small' sx={{ wordBreak: 'break-word' }}>
          {gerencia?.tiposDeServico?.[index]?.nome || ''}
        </Typography>
      </TableCell>
    </TableRow >
  ));

  return (rows);
};

export default function NewManages() {
  const [open, setOpen] = useState<boolean>(false);
  const [selectedManage, setSelectedManage] = useState<newManageType | null>(null);
  const { t } = useTranslation('translation');

  const getMuiTheme = () => createTheme({
    components: {
      MuiPaper: {
        styleOverrides: {
          root: {
            width: '100%'
          }
        }
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            textAlign: 'initial',
            padding: '10px'
          }
        }
      }
    }
  });

  const results = useQueries({
    queries: [
      {
        queryKey: ['GET_MANAGESv2'],
        queryFn: async () => {
          const token = await AttToken();
          if (token) {
            const response = await managesService.getAll(token);
            return response;
          }
        },
        retry: 2,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        staleTime: 5 * 60 * 1000,
      },
      {
        queryKey: ['GET_USERSV2'],
        queryFn: async () => {
          const token = await AttToken();
          if (token) {
            const response = await userService.getAllUsers(token);
            return response.users;
          }
        },
        retry: 2,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        staleTime: 5 * 60 * 1000,
      },
      {
        queryKey: ['GET_TEAMSV2'],
        queryFn: async () => {
          const token = await AttToken();
          if (token) {
            const response = await teamService.getSimpleTeams(token);
            return response;
          }
        },
        retry: 2,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        staleTime: 5 * 60 * 1000,
      },
      {
        queryKey: ['GET_SERVICESV2'],
        queryFn: async () => {
          const token = await AttToken();
          if (token) {
            const body = { ...token, empresa: token.userEmpresa };
            const response = await serviceTypeSerice.getAllByCompany(body);
            return response;
          }
        },
        retry: 2,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        staleTime: 5 * 60 * 1000,
      }
    ],
  });

  const isLoading = results?.some((result) => result.isLoading);
  const isFetching = results?.some((result) => result.isFetching);

  const loading = isLoading || isFetching;

  const columns = [
    {
      name: "nome",
      label: t('name'),
      options: {
        filter: false,
        sort: true,
        sortThirdClickReset: true,
        customBodyRender: (value: string) => (<Typography fontSize='small' sx={{ wordBreak: 'break-word' }}>{value}</Typography>)
      }
    },
    {
      name: 'dataCriacao',
      label: t('Managements.createdAt'),
      options: {
        filter: false,
        sort: true,
        sortThirdClickReset: true,
        customBodyRender: (value: number) => {
          const data = new Date(value).toLocaleString("pt-br");
          return data;
        },
      }
    },
    {
      name: 'status',
      label: t('status'),
      options: {
        customBodyRender: (value: boolean) => {
          return (
            !value
              ? <Typography color='error' fontWeight={700}>{`${t('inactive')}`}</Typography>
              : <Typography sx={{ color: '#00C4A7' }} fontWeight={700}>{t('active')}</Typography>
          );
        },
        filter: false,
        sort: true,
        sortThirdClickReset: true
      }
    },
    {
      name: "id",
      label: 'Editar/Ver',
      options: {
        customBodyRender: (value: string) => {
          return (
            <Button
              variant='outlined'
              endIcon={results?.[0]?.data?.find((el: newManageType) => el.id === value)?.geral
                ? <VisibilityIcon />
                : <BorderColorIcon />}
              onClick={() => {
                setSelectedManage(results?.[0]?.data?.find((el: newManageType) => el.id === value));
                setOpen(true);
              }}
            >
              {results?.[0]?.data?.find((el: newManageType) => el.id === value)?.geral ? t('view') : t('edit')}
            </Button>
          );
        },
        filter: false,
        sort: false,
      }
    },
    {
      name: "id",
      label: '',
      options: {
        filter: false,
        sort: false,
        display: false,
      }
    }
  ];

  const translatedTextLabels: MUIDataTableOptions = {
    textLabels: {
      body: {
        noMatch: `${t('table.noMatch')}`,
        toolTip: `${t('table.toolTip')}`,
        columnHeaderTooltip: column =>
          `${t('table.columnHeaderTooltip')} ${column.label}`
      },
      pagination: {
        next: `${t('table.next')}`,
        previous: `${t('table.previous')}`,
        rowsPerPage: `${t('table.rowsPerPage')}`,
        displayRows: `${t('table.displayRows')}`
      },
      toolbar: {
        search: `${t('table.search')}`,
        filterTable: `${t('table.filterTable')}`
      },
      filter: {
        title: `${t('table.title')}`,
        reset: `${t('table.reset')}`
      },
      viewColumns: {
        title: `${t('table.viewColumnsTitle')}`
      },
    },
  };

  const tableOptions: MUIDataTableOptions = {
    expandableRows: true,
    expandableRowsHeader: false,
    expandableRowsOnClick: false,
    selectableRowsHeader: false,
    selectableRowsHideCheckboxes: true,
    viewColumns: false,
    print: false,
    download: false,
    filter: false,
    responsive: 'simple',
    isRowExpandable: () => true,
    renderExpandableRow: (_rowData, rowMeta) => {
      const gerencia = results[0].data[rowMeta.dataIndex];
      return (
        <TableRow sx={{ padding: 2, margin: 2 }}>
          <TableCell colSpan={6}>
            {!(gerencia as newManageType)?.gerentes?.length
              && !(gerencia as newManageType)?.equipes?.length
              && !(gerencia as newManageType)?.tiposDeServico?.length
              ? <TableRow><TableCell align="center" colSpan={3}>
                <Typography textAlign='center' fontSize='large'>{t('Managements.semAssocia')}</Typography>
              </TableCell></TableRow>
              : <Paper sx={{ width: '100%', padding: 2, margin: 1 }}>
                <Typography variant="h6" gutterBottom component="div">
                  {t('checklists.formModal.associationTab')}
                </Typography>
                <Table size="small" aria-label="purchases">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('teamManage.modal.manages')}</TableCell>
                      <TableCell>{t('teamManage.modal.teams')}</TableCell>
                      <TableCell >{t('teamManage.register.kindOfService')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableComp gerencia={gerencia as newManageType} />
                  </TableBody>
                </Table>
              </Paper>
            }
          </TableCell>
        </TableRow>
      );
    },
  };

  if (results?.some((result) => result.isError)) {
    return <Typography>{t('Managements.manageErro')}</Typography>;
  }

  return (
    <>
      {open
        && <NewMgmtModal
          selectedManage={selectedManage}
          setSelectedManage={setSelectedManage}
          open={open}
          setOpen={setOpen}
          results={results}
        />}
      <Stack width='100%' direction="column" justifyContent='flex-start' alignItems='flex-start' spacing={1.5}>
        <Button
          disabled={loading}
          color='success'
          variant='contained'
          endIcon={<AddCircleIcon />}
          onClick={() => setOpen(true)}
        >
          {t('Managements.addManageBtn').slice(0, -1)}
        </Button>
        {loading
          ? <Skeleton variant="rounded" height={600} width='100%' />
          : <ThemeProvider theme={getMuiTheme()}>
            <MUIDataTable
              title={t('Managements.tabtitle')}
              data={results[0].data || []}
              columns={columns}
              options={{ ...tableOptions, ...translatedTextLabels }}
            />
          </ThemeProvider>}
      </Stack>
    </>
  );
}
